/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
  useCallback, useEffect, useRef, useState, useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { PageSizes } from 'pdf-lib';
import { getStandardTemplateV3, layoutHelpers, standardTemplateConstantsV3, getTimeTrackingGridPDFDesignerColumns } from 'ontraccr-form-to-pdf';
import GridLayout from 'react-grid-layout';
import { getIdMap, toTitleCase } from 'ontraccr-common/lib/Common';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import PDFDesignerItem from './PDFDesignerItemV3';
import PDFDesignerDrawer from '../PDFDesignerDrawer';
import PDFDesignerToolbar from '../PDFDesignerToolbar';
import PDFDesignerNameInput from '../PDFDesignerNameInput';
import PDFPageControls from '../../PDFPageControls';

import {
  addNewItem,
  pointDiff,
  resizeTableColumns,
  ONTRACCR_LOGO,
  formatTablePDFItemRows,
} from '../PDFDesignerHelpers';

import {
  AUTO_COLLECTED_ID,
} from '../../../../forms/forms.constants';
import { preparePDFFieldsFromSections } from '../../../../forms/formHelpers';
import { isNullOrUndefined, uuid } from '../../../../helpers/helpers';
import { MIN_COLUMN_WIDTH } from '../PDFDesigner.constants';
import CustomConfirmModal from '../../../modals/CustomConfirmModal';
import { calculateTextHeight } from '../../pdfHelpers';

import OttoPDFChat from '../../../../otto/OttoPDFChat';
import OttoPDFPill from '../../../../otto/OttoPDFPill';
import useOttoPDFDesigner from '../useOttoPDFDesigner';
import PDFRepeatableSectionLayoutModal from '../PDFRepeatableSectionLayoutModal';

const getDesignerContainerClass = (isDisplayMode, isEmbedded) => {
  if (isDisplayMode) return 'pdf-designer-container-display';
  if (isEmbedded) return 'pdf-designer-container-embedded';
  return 'pdf-designer-container';
};

const [width, height] = PageSizes.Letter; // W: 612, H: 792 LETTER
const SCALE = 1.0;

const NUM_COLS = 40;
const NUM_ROWS = 80;
const DEFAULT_RESIZE_HANDLES = ['s', 'e', 'se'];
const MARGINS = [0, 0];

export const CANVAS_HEIGHT = height * SCALE;
export const CANVAS_WIDTH = width * SCALE;
export const GRID_ROW_HEIGHT = Math.round((CANVAS_HEIGHT / NUM_ROWS) * 100) / 100;
export const GRID_COLUMN_WIDTH = Math.round((CANVAS_WIDTH / NUM_COLS) * 100) / 100;

const isItemWithinPageBounds = (item) => {
  const {
    gridProperties: {
      xGrid = 0,
      yGrid = 0,
      widthGrid = 0,
      heightGrid = 0,
    } = {},
  } = item ?? {};

  return xGrid >= 0
    && yGrid >= 0
    && xGrid + widthGrid <= NUM_COLS
    && yGrid + heightGrid <= NUM_ROWS;
};

const decorateDrawOptionsWithFiles = ({ drawOptions = [], fileMap = {} }) => {
  const seenIds = new Set();
  return drawOptions.map((page) => (
    page.map((item) => {
      const { type, image, id } = item;
      // Ensure every item has a unique id, react-grid-layout tracks items by id.
      // v2→v3 copied items may lack one, and v2 paste reused the source item's id
      const itemWithId = id && !seenIds.has(id) ? item : { ...item, id: uuid() };
      seenIds.add(itemWithId.id);
      const newItem = layoutHelpers.calculateGridProperties({ ...itemWithId, deletable: true });
      if (type !== 'image' || !image || !(image in fileMap)) return newItem;
      const {
        [image]: {
          jsFileObject,
        } = {},
      } = fileMap;
      return {
        ...newItem,
        image: jsFileObject,
      };
    })
  ));
};

const getInitialStartAtTop = (drawOptions = []) => {
  const newStart = [];
  drawOptions.forEach((page) => {
    const [{ startAtTop = false } = {}] = page;
    newStart.push(startAtTop);
  });
  return newStart;
};

export default function PDFDesigner({
  formRef,
  name,
  typeId,
  collected,
  sections,
  isDisplay,
  currentStep,
  onDrawOptionsChanged: updateDrawOptions,
  drawOptions = [],
  fileMap,
  useStandardTemplate,
  onUseStandardTemplateChange,
  isExternalForm,
  prevUseStandardTemplate,
  setPrevUseStandardTemplate,
  pdfName,
  setPDFName,
  repeatableSectionLayoutMode = false,
  lockedRepeatableLayoutSectionId = null,
  onRepeatableLayoutChange,
}) {
  const canvasRef = useRef();
  const {
    settings,
    companyImageURL: logo,
  } = useSelector((state) => state.settings.company);

  const [items, setItems] = useState(() => {
    let rawDrawOptions = drawOptions;
    if (repeatableSectionLayoutMode && !drawOptions?.length) {
      rawDrawOptions = [[]];
    }
    return decorateDrawOptionsWithFiles({ drawOptions: rawDrawOptions, fileMap });
  });
  const [editId, setEditId] = useState(null);
  const [moveId, setMoveId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [moveOffset, setMoveOffset] = useState();
  const [didMove, setDidMove] = useState(false);
  const [copyItem, setCopyItem] = useState();
  const [activePage, setActivePage] = useState(0);
  // Track whether page should start at top of a new page.
  const [startAtTopArray, setStartAtTopArray] = useState(() => {
    let pages = drawOptions;
    if (repeatableSectionLayoutMode && !drawOptions?.length) {
      pages = [[]];
    }
    return getInitialStartAtTop(pages);
  });
  const [realItems, setRealItems] = useState([]);
  const [shouldReloadDrawOptions, setShouldReloadDrawOptions] = useState(true);
  const [repeatableLayoutModalOpen, setRepeatableLayoutModalOpen] = useState(false);
  const [repeatableLayoutTargetItemId, setRepeatableLayoutTargetItemId] = useState(null);

  const isVisible = useMemo(() => (isDisplay || currentStep === 3), [isDisplay, currentStep]);
  const {
    ottoChatVisible,
    handleOttoChatToggle,
    handleOttoDrawOptionsUpdate,
    handleRestoreToInitial,
    handleUndo,
    canUndo,
    companySettings,
  } = useOttoPDFDesigner({
    drawOptions,
    logo,
    fileMap,
    updateDrawOptions,
    setItems,
    setRealItems,
    decorateDrawOptionsWithFiles,
    currentItems: items,
  });

  const relevantSections = useMemo(() => (
    preparePDFFieldsFromSections({ sections, isExternalForm })
  ), [isExternalForm, sections]);

  const configPropsMap = useMemo(() => {
    const map = {};
    relevantSections.forEach((section) => {
      const { fields = [] } = section;
      fields.forEach(({
        configProps,
        fieldId,
        id,
        selectedType,
      }) => {
        const realId = fieldId || id;
        map[realId] = {
          ...configProps,
          selectedType,
        };
      });
    });
    return map;
  }, [relevantSections]);

  const repeatableLayoutItem = useMemo(() => {
    if (!repeatableLayoutTargetItemId) return null;
    for (let p = 0; p < items.length; p += 1) {
      const found = items[p].find((i) => i.id === repeatableLayoutTargetItemId);
      if (found) return found;
    }
    return null;
  }, [items, repeatableLayoutTargetItemId]);

  const onOpenRepeatableSectionLayout = useCallback(() => {
    if (!editId) return;
    setRepeatableLayoutTargetItemId(editId);
    setRepeatableLayoutModalOpen(true);
  }, [editId]);

  const onClearRepeatableSectionLayout = useCallback(() => {
    if (!editId) return;
    CustomConfirmModal({
      title: 'Clear custom layout?',
      content: 'The repeatable section will use the standard PDF layout until you configure it again.',
      okText: 'Clear',
      cancelText: 'Cancel',
      onOk: () => {
        setItems((prev) => prev.map((page) => page.map((item) => {
          if (item.id !== editId) return item;
          const next = { ...item };
          delete next.sectionLayoutDrawOptions;
          return {
            ...next,
            drawOptions: { ...next.drawOptions },
          };
        })));
      },
    });
  }, [editId]);

  const onRepeatableLayoutSave = useCallback(({
    sectionLayoutDrawOptions,
  }) => {
    if (!repeatableLayoutTargetItemId) return;
    setItems((prev) => {
      const newItems = [...prev];
      const pageItems = newItems[activePage];
      const newPageItems = pageItems.map((item) => {
        if (item.id !== repeatableLayoutTargetItemId) return item;
        return { ...item, sectionLayoutDrawOptions };
      });
      newItems[activePage] = newPageItems;
      return newItems;
    });
    setRepeatableLayoutModalOpen(false);
    setRepeatableLayoutTargetItemId(null);
  }, [repeatableLayoutTargetItemId, activePage]);

  const onMoveStart = useCallback((id) => (offset) => {
    setMoveId(id);
    setMoveOffset(offset);
  }, []);

  const onMouseUp = useCallback(() => {
    setMoveId(null);
    setMoveOffset({});
    if (moveId !== null && !didMove) {
      setEditing(true);
      setEditId(moveId);
    }
    setDidMove(false);
  }, [moveId, didMove]);

  const onMouseMove = useCallback((e) => {
    if (!isVisible || (isNullOrUndefined(moveId))) return;
    if (moveOffset) {
      // Consider a mouse move action as a 'move' if moving more than 20 px
      const { clientX, clientY } = e;
      const diffFromStart = pointDiff({ x: clientX, y: clientY }, moveOffset);
      setDidMove(diffFromStart > 20);
    }
  }, [moveId, moveOffset, isVisible]);

  const toggleEditing = useCallback(() => {
    setEditId(null);
    setTimeout(() => setEditing(false), [500]);
  }, []);

  const onDrawOptionChanged = useCallback((data = {}) => {
    const newItems = [...items];
    const pageItems = newItems[activePage];
    const ourItem = pageItems.find((item) => item.id === editId);
    const newPageItems = pageItems.map((item) => {
      if (item.id === editId) {
        let newHeight = data.height ?? ourItem.drawOptions.height;

        if (item.type === 'table') {
          const newRowHeight = data.rowHeight ?? ourItem.drawOptions.rowHeight ?? GRID_ROW_HEIGHT;
          const newHeaderHeight = data.headerOptions?.headerHeight
            ?? ourItem.drawOptions?.headerOptions?.headerHeight ?? GRID_ROW_HEIGHT;

          newHeight = newRowHeight + newHeaderHeight;
        }

        return layoutHelpers.calculateGridProperties({
          ...resizeTableColumns({ data, ourItem }),
          drawOptions: { ...ourItem.drawOptions, ...data, height: newHeight },
          isUpdated: true,
        });
      }
      return item;
    });
    newItems[activePage] = newPageItems;

    setItems(newItems);
  }, [editId, items, activePage]);

  const activeItems = useMemo(() => realItems[activePage] ?? [], [realItems, activePage]);

  const activeItemsWithHeaderAndFooter = useMemo(() => {
    const header = {
      type: 'header',
      id: 'header',
      drawOptions: {
        ...standardTemplateConstantsV3.defaultDrawOptions,
        x: 0,
        y: 0,
        width: CANVAS_WIDTH,
        height: GRID_ROW_HEIGHT,
      },
      gridProperties: {
        xGrid: 0,
        yGrid: 0,
        widthGrid: NUM_COLS,
        heightGrid: 1,
      },
    };

    const footer = {
      type: 'footer',
      id: 'footer-pdf',
      drawOptions: {
        ...standardTemplateConstantsV3.defaultDrawOptions,
        x: 0,
        y: CANVAS_HEIGHT - GRID_ROW_HEIGHT,
        width: CANVAS_WIDTH,
        height: GRID_ROW_HEIGHT,
      },
      gridProperties: {
        xGrid: 0,
        yGrid: NUM_ROWS - 1,
        widthGrid: NUM_COLS,
        heightGrid: 1,
      },
    };

    return [header, ...activeItems, footer];
  }, [activeItems]);

  const occupiedGrid = useMemo(
    () => layoutHelpers.createOccupiedGrid(activeItemsWithHeaderAndFooter),
    [activeItemsWithHeaderAndFooter],
  );

  const onItemAdd = useCallback((type) => {
    const newItems = [...items];
    if (type === 'page') {
      newItems.push([]);
      setItems(newItems);
      setStartAtTopArray(startAtTopArray.concat([false]));
      return;
    }

    if (type === 'copyPage') {
      const ourPage = [...items[activePage]];
      newItems.push(ourPage.map((item) => ({ ...item, id: uuid() }))); // new uuid for copied items
      setItems(newItems);
      setStartAtTopArray(startAtTopArray.concat([false]));
      return;
    }

    const newItem = addNewItem(type, true);
    if (!newItem) return;
    const parsedNewItem = layoutHelpers.calculateGridProperties(newItem);

    const emptySpot = layoutHelpers.placeItemInEmptyGridSpot(
      parsedNewItem,
      occupiedGrid,
    );
    if (!emptySpot) {
      return CustomConfirmModal({ // eslint-disable-line consistent-return
        title: `${toTitleCase(type)} does not fit`,
        content: 'The selected item does not fit in the current layout with its default dimensions. Please adjust the layout to fit the item.',
        hideCancel: true,
      });
    }

    const {
      xGrid,
      yGrid,
      x,
      y,
    } = emptySpot;

    parsedNewItem.drawOptions = {
      ...parsedNewItem.drawOptions,
      x,
      y,
    };
    parsedNewItem.gridProperties = {
      ...parsedNewItem.gridProperties,
      xGrid,
      yGrid,
    };
    if (repeatableSectionLayoutMode && lockedRepeatableLayoutSectionId) {
      if (type === 'text' || type === 'table') {
        parsedNewItem.link = { sectionId: lockedRepeatableLayoutSectionId };
      }
    }
    const pageItems = [...newItems[activePage]];
    pageItems.push(parsedNewItem);
    newItems[activePage] = pageItems;
    setItems(newItems);
    toggleEditing();
  }, [
    items,
    startAtTopArray,
    activePage,
    toggleEditing,
    occupiedGrid,
    repeatableSectionLayoutMode,
    lockedRepeatableLayoutSectionId,
  ]);

  const onDelete = useCallback(() => {
    if (editId === null) return;
    const newItems = [...items];
    const pageItems = newItems[activePage];
    const newPageItems = pageItems.filter((item) => item.id !== editId);
    newItems[activePage] = newPageItems;
    setItems(newItems);
    toggleEditing();
  }, [items, activePage, editId, toggleEditing]);

  const onTextChanged = useCallback((id) => (text) => {
    const newItems = [...items];
    const pageItems = newItems[activePage];
    const newPageItems = pageItems.map((item) => {
      if (item.id === id) {
        const {
          tags,
          drawOptions: newDrawOptions,
          ...rest
        } = item;
        // Remove linkTextStyle from drawOptions as properties are in rich text content
        const {
          linkTextStyle,
          ...restDrawOptions
        } = newDrawOptions;
        return {
          ...rest,
          isUpdated: true,
          text,
          drawOptions: {
            ...restDrawOptions,
          },
        };
      }
      return item;
    });
    newItems[activePage] = newPageItems;
    setItems(newItems);
  }, [items, activePage]);

  // for applying styles to linked fields
  const onApplyTextStyles = useCallback((id) => (styles) => {
    const newItems = [...items];
    const pageItems = newItems[activePage];
    const newPageItems = pageItems.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          drawOptions: {
            ...item?.drawOptions,
            linkTextStyle: {
              ...item?.drawOptions?.linkTextStyle,
              ...styles,
            },
          },
        };
      }
      return item;
    });
    newItems[activePage] = newPageItems;
    setItems(newItems);
  }, [items, activePage]);

  const onIsSectionHeaderChanged = useCallback((id) => (isSectionHeader) => {
    const newItems = [...items];
    const pageItems = newItems[activePage];
    const newPageItems = pageItems.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          isUpdated: true,
          isSectionHeader,
        };
      }
      return item;
    });
    newItems[activePage] = newPageItems;
    setItems(newItems);
  }, [items, activePage]);

  const onImageAdd = useCallback((id) => (image) => {
    const newItems = [...items];
    const pageItems = newItems[activePage];
    const newPageItems = pageItems.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          isUpdated: true,
          image,
        };
      }
      return item;
    });
    newItems[activePage] = newPageItems;
    setItems(newItems);
  }, [items, activePage]);

  const onPageChanged = useCallback((newPage) => {
    setActivePage(newPage - 1);
    toggleEditing();
  }, [toggleEditing]);

  const onPageDeleted = useCallback(() => {
    const newItems = [...items];
    newItems.splice(activePage, 1);
    setItems(newItems);

    const newSetArray = [...startAtTopArray];
    newSetArray.splice(activePage, 1);
    setStartAtTopArray(newSetArray);

    const newActivePage = activePage >= newItems.length ? newItems.length - 1 : activePage;
    setActivePage(newActivePage);
  }, [items, startAtTopArray, activePage]);

  const onTopOfPageChecked = useCallback((checked) => {
    setStartAtTopArray(
      startAtTopArray.map((val, idx) => {
        if (idx === activePage) return checked;
        return val;
      }),
    );
  }, [startAtTopArray, activePage]);

  const onItemPageChanged = useCallback((newPage) => {
    const newItems = [...items];
    const pageItems = newItems[activePage];
    const editItem = pageItems.find((item) => item.id === editId);
    const newOldPageItems = pageItems.filter((item) => item.id !== editId);
    newItems[activePage] = newOldPageItems;
    newItems[newPage].push(editItem);
    setActivePage(newPage);
    setItems(newItems);
  }, [activePage, editId, items]);

  const onLinkChanged = useCallback(({ sectionId, fieldId }) => {
    const newItems = [...items];
    const pageItems = newItems[activePage];
    const editItem = pageItems.find((item) => item.id === editId);
    const newItem = { ...editItem };
    if (sectionId) {
      if (fieldId) {
        const fieldKey = sectionId === AUTO_COLLECTED_ID ? 'collectedId' : 'fieldId';
        newItem.link = { sectionId, [fieldKey]: fieldId };
      } else {
        newItem.link = { sectionId };
      }
      if (newItem.type === 'repeatableSection') {
        if (sectionId !== editItem.link?.sectionId) {
          delete newItem.sectionLayoutDrawOptions;
        }
      }
    } else if (fieldId) {
      const fieldKey = newItem.link.sectionId === AUTO_COLLECTED_ID ? 'collectedId' : 'fieldId';
      newItem.link = { sectionId: newItem.link.sectionId, [fieldKey]: fieldId };
    } else {
      // Cleared sectionId
      newItem.link = {};
      if (newItem.type === 'repeatableSection') {
        delete newItem.sectionLayoutDrawOptions;
      }
    }

    if (newItem.type === 'table' && newItem.link.sectionId && newItem.link.fieldId) {
      // Apply field columns to table
      let columns = [];
      const linkedFieldType = configPropsMap[newItem.link.fieldId]?.selectedType;
      const isWeather = linkedFieldType === 'weather';
      const isTimeTrackingGrid = linkedFieldType === 'timeTrackingGrid';
      if (isWeather) {
        columns = [{ name: 'Weather Report' }];
      } else if (isTimeTrackingGrid) {
        columns = getTimeTrackingGridPDFDesignerColumns();
      } else {
        const {
          fields: ourFields = [],
        } = relevantSections.find((section) => section.id === newItem.link.sectionId) || {};
        const {
          configProps: {
            columns: ourColumns = [],
          } = {},
        } = ourFields.find((field) => field.id === newItem.link.fieldId) || {};
        columns = ourColumns;
      }
      if (columns) {
        const newRows = formatTablePDFItemRows(columns, newItem.rows);
        if (newRows) {
          newItem.rows = newRows;
        }
      }
    }

    const newPageItems = pageItems.map((item) => {
      if (item.id === editId) {
        return newItem;
      }
      return item;
    });
    newItems[activePage] = newPageItems;
    setItems(newItems);
  }, [activePage, editId, items, relevantSections, configPropsMap]);

  const onTableResize = useCallback((itemId) => ({ index: columnIndex, deltaX }) => {
    const newItems = [...items];
    const pageItems = newItems[activePage];
    const resizeItem = pageItems.find((item) => item.id === itemId);
    const {
      rows = [],
      drawOptions: {
        width: totalWidth,
      } = {},
    } = resizeItem;

    // Calculate new column widths based on the first row
    const firstRow = rows[0];
    const {
      cols: firstRowCols = [],
    } = firstRow;
    const colCount = firstRowCols.length;

    const currentWidths = firstRowCols.map((col) => {
      if (col.width) return col.width;
      return totalWidth / (colCount || 1);
    });

    // Calculate the new width for the resized column
    const currentResizeColWidth = currentWidths[columnIndex];
    const newResizeColWidth = Math.max(currentResizeColWidth + deltaX, MIN_COLUMN_WIDTH);
    const actualDeltaX = newResizeColWidth - currentResizeColWidth;

    // Distribute the width change to adjacent columns
    const newWidths = [...currentWidths];
    newWidths[columnIndex] = newResizeColWidth;

    // Try to distribute the change to the right column first, then left
    let remainingChange = -actualDeltaX;

    // Try right column first
    if (columnIndex < colCount - 1 && remainingChange !== 0) {
      const rightColIndex = columnIndex + 1;
      const rightColCurrentWidth = newWidths[rightColIndex];
      const rightColNewWidth = Math.max(rightColCurrentWidth + remainingChange, MIN_COLUMN_WIDTH);
      const rightColActualChange = rightColNewWidth - rightColCurrentWidth;
      newWidths[rightColIndex] = rightColNewWidth;
      remainingChange -= rightColActualChange;
    }

    // If still have remaining change, try left column
    if (columnIndex > 0 && remainingChange !== 0) {
      const leftColIndex = columnIndex - 1;
      const leftColCurrentWidth = newWidths[leftColIndex];
      const leftColNewWidth = Math.max(leftColCurrentWidth + remainingChange, MIN_COLUMN_WIDTH);
      newWidths[leftColIndex] = leftColNewWidth;
    }

    // Apply the new widths to all rows
    const newRows = rows.map((row) => {
      const { cols = [] } = row;
      const newCols = cols.map((col, cIndex) => ({
        ...col,
        width: parseInt(newWidths[cIndex], 10),
      }));

      return {
        ...row,
        cols: newCols,
      };
    });

    const newPageItems = pageItems.map((item) => {
      if (item.id === itemId) {
        return {
          ...item,
          rows: newRows,
          drawOptions: {
            ...item.drawOptions,
            width: totalWidth, // keep same width
          },
        };
      }
      return item;
    });
    newItems[activePage] = newPageItems;
    setItems(newItems);
  }, [items, activePage]);

  const onLayoutChange = useCallback((newLayout) => {
    // HARBOUR-10325
    // realItems !== items means the grid is still showing a layout that is not derived from
    // items (e.g. right after toggling standard -> custom). Writing that back would corrupt
    // items and ping-pong with realItems until React throws max update depth.
    if (useStandardTemplate || isDisplay || realItems !== items) return;

    const newItems = [...items];
    const pageItems = newItems[activePage];
    const newLayoutMap = getIdMap(newLayout, 'i');
    const newPageItems = pageItems?.map((item) => {
      const { id } = item;
      const newLayoutItem = newLayoutMap[id];
      if (!newLayoutItem) return item;

      // Convert grid coordinates back to pixels
      const pixelX = newLayoutItem.x * GRID_COLUMN_WIDTH;
      const pixelY = newLayoutItem.y * GRID_ROW_HEIGHT;
      const pixelW = newLayoutItem.w * GRID_COLUMN_WIDTH;
      const pixelH = newLayoutItem.h * GRID_ROW_HEIGHT;

      const newItem = {
        ...item,
        ...resizeTableColumns({ data: { width: pixelW }, ourItem: item }),
        drawOptions: {
          ...item.drawOptions,
          x: pixelX,
          y: pixelY,
          width: pixelW,
          height: pixelH,
        },
      };

      return layoutHelpers.calculateGridProperties(newItem);
    }) ?? [];
    newItems[activePage] = newPageItems;
    setItems(newItems);
  }, [items, realItems, activePage, useStandardTemplate, isDisplay]);

  useEffect(() => {
    const callback = () => {
      // Nested layout editor owns clipboard while its modal is open
      if (repeatableLayoutModalOpen) return;
      if (editId === null) return;
      const pageItems = items[activePage];
      setCopyItem({
        item: pageItems.find((item) => item.id === editId),
        page: activePage,
      });
    };

    document.addEventListener('copy', callback);
    return () => document.removeEventListener('copy', callback);
  }, [editId, items, activePage, repeatableLayoutModalOpen]);

  useEffect(() => {
    const callback = () => {
      // Nested layout editor owns clipboard while its modal is open
      if (repeatableLayoutModalOpen) return;
      if (!copyItem) return;
      const { item: copiedItem } = copyItem;
      if (repeatableSectionLayoutMode && copiedItem.type === 'repeatableSection') return;
      const newItems = [...items];
      const pageItems = [...newItems[activePage]];
      const newItem = {
        ...copiedItem,
        // Need to spread or changes will affect both items
        drawOptions: { ...copiedItem.drawOptions },
        deletable: true,
        id: uuid(),
      };
      const parsedNewItem = layoutHelpers.calculateGridProperties(newItem);
      const emptySpot = layoutHelpers.placeItemInEmptyGridSpot(
        parsedNewItem,
        occupiedGrid,
      );
      if (!emptySpot) {
        CustomConfirmModal({
          title: `${toTitleCase(copiedItem.type)} does not fit`,
          content: 'The copied item does not fit on the current page. Please make room or add a new page.',
          hideCancel: true,
        });
        return;
      }
      const {
        xGrid,
        yGrid,
        x,
        y,
      } = emptySpot;
      parsedNewItem.drawOptions = {
        ...parsedNewItem.drawOptions,
        x,
        y,
      };
      parsedNewItem.gridProperties = {
        ...parsedNewItem.gridProperties,
        xGrid,
        yGrid,
      };
      pageItems.push(parsedNewItem);
      newItems[activePage] = pageItems;
      setItems(newItems);
    };

    document.addEventListener('paste', callback);
    return () => document.removeEventListener('paste', callback);
  }, [
    copyItem,
    items,
    activePage,
    repeatableSectionLayoutMode,
    occupiedGrid,
    repeatableLayoutModalOpen,
  ]);

  useEffect(() => {
    if (repeatableSectionLayoutMode) return;
    const lenStart = startAtTopArray.length;
    const mergedItems = realItems.map((page, pageIdx) => {
      if (pageIdx >= lenStart) {
        // Something weird happened.
        // Both arrays should be the same length
        return page;
      }
      const inBoundsPage = page.filter(isItemWithinPageBounds);
      if (inBoundsPage.length === 0) return inBoundsPage;
      const startAtTop = startAtTopArray[pageIdx];
      return inBoundsPage.map((item) => ({ ...item, startAtTop }));
    });

    updateDrawOptions(mergedItems);
  }, [realItems, startAtTopArray, updateDrawOptions, repeatableSectionLayoutMode]);

  useEffect(() => {
    setActivePage(0);
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === 3) setShouldReloadDrawOptions(true);
  }, [currentStep]);

  const isSectionDuplicationEnabled = useMemo(() => sections?.some((section) => {
    const { settings: { isDuplicationEnabled = false } = {} } = section;
    return isDuplicationEnabled;
  }), [sections]);

  /**
   * If the user opens an existing form template and makes changes to the visible columns of a
   * table, the drawOptions do not update to reflect the changes. To counter this, the below
   * useEffect will update all table fields with an updated version of the rows field based on the
   * columns array in the configProps of each respective table field
   *
   * The state shouldReloadDrawOptions is used to prevent looping the useEffect and to ensure it is
   * only run once when the user opens the PDF Designer step. This also allows rerunning the
   * useEffect if the user decides to go back to the Fields tab and update the visible columns
   * before submitting the template changes
   */
  useEffect(() => {
    if (useStandardTemplate
        || !shouldReloadDrawOptions
        || currentStep !== 3
        || !items?.length
        || repeatableSectionLayoutMode
    ) return;

    const newItems = items.map((pages) => {
      const newPage = pages.map((item) => {
        const {
          type,
          link: {
            fieldId,
          } = {},
          rows,
        } = item;
        if (type !== 'table' || !fieldId) return item;

        const linkedFieldType = configPropsMap[fieldId]?.selectedType;
        let columns = [];
        if (linkedFieldType === 'timeTrackingGrid') {
          columns = getTimeTrackingGridPDFDesignerColumns();
        } else if (linkedFieldType !== 'weather') {
          columns = configPropsMap[fieldId]?.columns ?? [];
        }
        if (!columns.length) return item;

        const newRows = formatTablePDFItemRows(columns, rows);
        if (!newRows) return item;
        return {
          ...item,
          rows: newRows,
        };
      });

      return newPage;
    });

    setItems(newItems);
    setShouldReloadDrawOptions(false);
  }, [items, useStandardTemplate, currentStep, repeatableSectionLayoutMode]);

  /**
   * If the user toggles the standard template, we need to reset the draw options
   * This handles the initial load for custom templates
   *
   * The reason why we compare to the previous state is because we do not want to reset
   * the draw options if the user is editing the form or if they edit a form that already is using
   * custom draw options
   */
  useEffect(() => {
    const setPdf = async () => {
      let ourName = name;
      if (!isDisplay && formRef && formRef.current) {
        // Need to dynamically pull the user entered name for the form
        // when editing or adding a form
        const { name: formName } = formRef.current.getFieldsValue();
        ourName = formName;
      }

      const newItems = await getStandardTemplateV3({
        settings: {
          ...settings,
          logo: logo || ONTRACCR_LOGO,
        },
        name: ourName,
        sections: relevantSections,
        collected,
        originalDrawOptions: items,
        convertRepeatableSections: true,
      });

      setItems(newItems);
      setPrevUseStandardTemplate(useStandardTemplate);
    };

    if (
      (!items.length || prevUseStandardTemplate !== useStandardTemplate)
      && currentStep === 3
      && !useStandardTemplate
      && !repeatableSectionLayoutMode
    ) {
      setPdf();
    }
  }, [
    collected,
    name,
    formRef,
    relevantSections,
    settings,
    isDisplay,
    currentStep,
    logo,
    items,
    useStandardTemplate,
    prevUseStandardTemplate,
    setPrevUseStandardTemplate,
    repeatableSectionLayoutMode,
  ]);

  // Initial load of standard template
  useEffect(() => {
    const initPDF = async () => {
      if (repeatableSectionLayoutMode) {
        setRealItems(items);
        return;
      }
      if (useStandardTemplate) {
        let ourName = name;

        if (!isDisplay && formRef?.current) {
          const { name: formName } = formRef.current.getFieldsValue();
          ourName = formName;
        }

        const newItems = await getStandardTemplateV3({
          settings: {
            ...settings,
            logo: logo || ONTRACCR_LOGO,
            showHiddenFields: true,
          },
          name: ourName,
          sections: relevantSections,
          collected,
          originalDrawOptions: items,
        });

        setRealItems(newItems);
        return;
      }

      setRealItems(items);
    };

    initPDF();
  }, [
    repeatableSectionLayoutMode,
    useStandardTemplate,
    collected,
    isDisplay,
    formRef,
    name,
    settings,
    relevantSections,
    logo,
    items,
    fileMap,
    isExternalForm,
  ]);

  const currentLayout = useMemo(() => (
    activeItemsWithHeaderAndFooter.map((item) => {
      const {
        drawOptions: {
          x,
          y,
          width: itemWidth,
          height: itemHeight,
          fontSize,
        } = {},
        text,
        id,
        type,
      } = item ?? {};

      let finalHeight = itemHeight;

      // In standard template mode, calculate height for text items
      if (useStandardTemplate && type === 'text' && text) {
        let parsedText = text;
        if (Array.isArray(text)) parsedText = text.map((segment) => segment.content).join('\n'); // Rich text
        const calculatedHeight = calculateTextHeight(parsedText, fontSize, itemWidth);
        if (calculatedHeight && calculatedHeight > itemHeight) {
          finalHeight = calculatedHeight;
        }
      }

      // Convert pixel coordinates to grid units
      const gridX = Math.round(x / GRID_COLUMN_WIDTH);
      const gridY = Math.round(y / GRID_ROW_HEIGHT);
      const gridW = Math.round(itemWidth / GRID_COLUMN_WIDTH);
      const gridH = Math.round(finalHeight / GRID_ROW_HEIGHT);

      const layoutObj = {
        i: id,
        x: gridX,
        y: gridY,
        w: gridW,
        h: gridH,
        resizeHandles: DEFAULT_RESIZE_HANDLES,
      };

      if (item.type === 'table') layoutObj.resizeHandles = ['w', 'e'];

      if (item.type === 'repeatableSection') {
        // Full-width, only vertical resize
        layoutObj.x = 0;
        layoutObj.w = NUM_COLS;
        layoutObj.resizeHandles = ['s'];
      }

      if (item.type === 'header' || item.type === 'footer') {
        layoutObj.static = true;
      }

      return layoutObj;
    })
  ), [activeItemsWithHeaderAndFooter, useStandardTemplate]);

  const editField = useMemo(() => {
    const pageItems = realItems[activePage];
    const relevantField = editId !== null
      ? pageItems.find((item) => item.id === editId)
      : undefined;
    if (useStandardTemplate && !relevantField?.isHeader) return undefined;
    return relevantField;
  }, [realItems, activePage, editId, useStandardTemplate]);

  useEffect(() => {
    if (!repeatableSectionLayoutMode || !onRepeatableLayoutChange) return;
    onRepeatableLayoutChange({
      pages: realItems.map((page) => page.filter(isItemWithinPageBounds)),
    });
  }, [
    repeatableSectionLayoutMode,
    onRepeatableLayoutChange,
    realItems,
  ]);

  return (
    <>
      {!repeatableSectionLayoutMode && (
        <PDFDesignerNameInput
          isDisplay={isDisplay}
          isSectionDuplicationEnabled={isSectionDuplicationEnabled}
          onTextChange={setPDFName}
          text={pdfName}
          sections={relevantSections}
        />
      )}
      {!isDisplay && (
        <PDFDesignerToolbar
          onItemAdd={onItemAdd}
          numberOfPages={realItems.length}
          onPageDeleted={onPageDeleted}
          onTopOfPageChecked={onTopOfPageChecked}
          startAtTop={activePage < startAtTopArray.length ? startAtTopArray[activePage] : false}
          useStandardTemplate={useStandardTemplate}
          onUseStandardTemplateChange={onUseStandardTemplateChange}
          isSectionDuplicationEnabled={
            repeatableSectionLayoutMode ? false : isSectionDuplicationEnabled
          }
          embeddedLayoutEditor={repeatableSectionLayoutMode}
        />
      )}
      <div
        className={getDesignerContainerClass(isDisplay, repeatableSectionLayoutMode)}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
      >
        <div
          style={{
            flexDirection: 'column',
            height: 'fit-content',
            pointerEvents: 'auto',
          }}
        >
          <div
            ref={canvasRef}
            style={{
              height: CANVAS_HEIGHT,
              width: CANVAS_WIDTH,
            }}
            id="pdf-designer-canvas"
          >
            <GridLayout
              width={CANVAS_WIDTH}
              maxRows={NUM_ROWS}
              rowHeight={GRID_ROW_HEIGHT}
              cols={NUM_COLS}
              margin={MARGINS}
              layout={currentLayout}
              isResizable={!useStandardTemplate && !isDisplay}
              isDraggable={!useStandardTemplate && !isDisplay}
              compactType={null}
              preventCollision
              onLayoutChange={onLayoutChange}
            >
              {activeItemsWithHeaderAndFooter.map((item) => (
                <div key={item.id}>
                  <PDFDesignerItem
                    isDisplay={isDisplay || (!item.isHeader && useStandardTemplate)}
                    onImageAdd={onImageAdd(item.id)}
                    onMoveStart={onMoveStart(item.id)}
                    editing={editId === item.id}
                    onTableResize={onTableResize(item.id)}
                    sections={relevantSections}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...item}
                  />
                </div>
              ))}
            </GridLayout>
          </div>
        </div>
        <PDFPageControls
          numberOfPages={realItems.length}
          activePage={activePage + 1}
          onActivePageChange={onPageChanged}
        />
        {editing && (
          <PDFDesignerDrawer
            open={editId !== null}
            onClose={toggleEditing}
            onDrawOptionChanged={onDrawOptionChanged}
            onDelete={onDelete}
            field={editField}
            page={activePage}
            numberOfPages={realItems.length}
            onPageChanged={onItemPageChanged}
            onTextChanged={onTextChanged(editId)}
            onApplyTextStyles={onApplyTextStyles(editId)}
            onIsSectionHeaderChanged={onIsSectionHeaderChanged(editId)}
            onImageAdd={onImageAdd(editId)}
            onLinkChanged={onLinkChanged}
            onOpenRepeatableSectionLayout={
              repeatableSectionLayoutMode ? null : onOpenRepeatableSectionLayout
            }
            onClearRepeatableSectionLayout={
              repeatableSectionLayoutMode ? null : onClearRepeatableSectionLayout
            }
            sections={relevantSections}
            collected={collected}
            typeId={typeId}
            useStandardTemplate={useStandardTemplate}
            lockedRepeatableLayoutSectionId={(
              repeatableSectionLayoutMode ? lockedRepeatableLayoutSectionId : null
            )}
            zIndex={repeatableSectionLayoutMode ? 1200 : undefined}
            useV3
          />
        )}
      </div>
      {!isDisplay && !useStandardTemplate && !repeatableSectionLayoutMode && (
        <OttoPDFPill onClick={handleOttoChatToggle} />
      )}
      <OttoPDFChat
        visible={ottoChatVisible}
        onClose={handleOttoChatToggle}
        formData={{
          name,
          sections: relevantSections,
          collected,
          typeId,
        }}
        completedForm={{
          sections: relevantSections,
          collected,
          data: { collected },
        }}
        currentDrawOptions={drawOptions}
        onDrawOptionsUpdate={handleOttoDrawOptionsUpdate}
        onRestore={handleRestoreToInitial}
        onUndo={handleUndo}
        canUndo={canUndo}
        companySettings={companySettings}
      />
      {!repeatableSectionLayoutMode && (
        <PDFRepeatableSectionLayoutModal
          open={repeatableLayoutModalOpen}
          onCancel={() => {
            setRepeatableLayoutModalOpen(false);
            setRepeatableLayoutTargetItemId(null);
          }}
          onSave={onRepeatableLayoutSave}
          linkedSectionId={repeatableLayoutItem?.link?.sectionId}
          relevantSections={relevantSections}
          fileMap={fileMap}
          initialSectionLayoutDrawOptions={repeatableLayoutItem?.sectionLayoutDrawOptions}
          formRef={formRef}
          name={name}
          typeId={typeId}
          collected={collected}
          isExternalForm={isExternalForm}
          modalKey={`${repeatableLayoutTargetItemId ?? ''}-${repeatableLayoutModalOpen}`}
        />
      )}
    </>
  );
}
